import React, { useState, useMemo } from "react";
import { useAppContext } from "../context";
import styles from "../styles/BuiltUpAreaCalculator.module.css";

const AncillaryFactorCalculator = ({ onPrev }) => {
  const { plotData } = useAppContext();

  const [resArea, setResArea] = useState("");
  const [nonResArea, setNonResArea] = useState("");
  const [tdrArea, setTdrArea] = useState("");
  const [calculated, setCalculated] = useState(false);

  const totalBuiltUp = Number(resArea || 0) + Number(nonResArea || 0);

  const {
    grossPlotArea,
    netPlotArea,
    totalReservation,
    basicFSIFactor,
    premiumFSIFactor,
    maxTDRFactor,
    tdrFactor
  } = plotData;

  // --- FSI Calculations ---
  const results = useMemo(() => {
    const resAreaNum = Number(resArea || 0);
    const nonResAreaNum = Number(nonResArea || 0);
    const totalBuiltUp = resAreaNum + nonResAreaNum;

    // --- Ancillary Factor ---
    const resPercent = totalBuiltUp > 0 ? resAreaNum / totalBuiltUp : 0;
    const nonResPercent = totalBuiltUp > 0 ? nonResAreaNum / totalBuiltUp : 0;
    const resAncillary = 0.6;
    const nonResAncillary = 0.8;
    const totalAncillaryFactor = resPercent * resAncillary + nonResPercent * nonResAncillary;

    // --- Basic FSI ---
    const BasicFSI_Potential = netPlotArea * basicFSIFactor;
    const BasicFSI_Utilised = Math.min(BasicFSI_Potential, totalBuiltUp);

    // --- TDR calculations ---
    const ReservationTDR = totalReservation * tdrFactor;
    const PurchasedTDR = Number(tdrArea || 0);
    const TotalTDRAvailable = ReservationTDR + PurchasedTDR;

    const MaxPermissibleTDR = grossPlotArea * maxTDRFactor;
    const MaxUtilizableTDR = Math.min(TotalTDRAvailable, MaxPermissibleTDR);
    const MaxUtilizableTDR_Utilised = Math.min(MaxUtilizableTDR, totalBuiltUp - BasicFSI_Utilised);

    // --- Ancillary FSI for (Basic FSI + TDR) ---
    const AncillaryFSI_Basic_TDR_Potential = (BasicFSI_Potential + MaxUtilizableTDR) * totalAncillaryFactor;
    const AncillaryFSI_Basic_TDR_Utilised = Math.min(
      AncillaryFSI_Basic_TDR_Potential,
      totalBuiltUp - BasicFSI_Utilised - MaxUtilizableTDR_Utilised
    );

    // --- Premium FSI ---
    const PremiumFSI_Potential = grossPlotArea * premiumFSIFactor;
    const PremiumFSI_Utilised = Math.min(
      PremiumFSI_Potential,
      totalBuiltUp - BasicFSI_Utilised - MaxUtilizableTDR_Utilised - AncillaryFSI_Basic_TDR_Utilised
    );

    // --- Ancillary FSI for Premium FSI ---
    const AncillaryFSI_Premium_Potential = PremiumFSI_Potential * totalAncillaryFactor;
    const AncillaryFSI_Premium_Utilised = Math.min(
      AncillaryFSI_Premium_Potential,
      totalBuiltUp - BasicFSI_Utilised - MaxUtilizableTDR_Utilised - AncillaryFSI_Basic_TDR_Utilised - PremiumFSI_Utilised
    );

    // --- Total Max Built-Up ---
    const TotalMaxBuiltUpPotential =
      BasicFSI_Potential +
      MaxUtilizableTDR +
      AncillaryFSI_Basic_TDR_Potential +
      PremiumFSI_Potential +
      AncillaryFSI_Premium_Potential;

    const TotalMaxBuiltUpUtilised =
      BasicFSI_Utilised +
      MaxUtilizableTDR_Utilised +
      AncillaryFSI_Basic_TDR_Utilised +
      PremiumFSI_Utilised +
      AncillaryFSI_Premium_Utilised;

    // --- Notes ---
    const permissibleCheck =
      totalBuiltUp <= TotalMaxBuiltUpPotential
        ? "Proposed B.U. Area is within permissible development potential."
        : `Proposed B.U. Area is ${Math.round(totalBuiltUp - TotalMaxBuiltUpPotential)} Sq.m. more than permissible development potential.`;

    const fsiUsageNote =
      totalBuiltUp > BasicFSI_Utilised + MaxUtilizableTDR_Utilised
        ? "Basic FSI and TDR is consumed fully and for additional built-up area, premium/ancillary FSI must be used."
        : "Basic FSI and TDR is not consumed fully.";

    // --- Ancillary / Premium Utilised ---
    const AncillaryFSI_Utilised = AncillaryFSI_Basic_TDR_Utilised + AncillaryFSI_Premium_Utilised;
    const PremiumArea_Utilised = PremiumFSI_Utilised;
    const ExcessDevelopmentRight = Math.max(0, TotalTDRAvailable - MaxUtilizableTDR);

    return {
      BasicFSI_Potential,
      BasicFSI_Utilised,
      ReservationTDR,
      TotalTDRAvailable,
      MaxPermissibleTDR,
      MaxUtilizableTDR,
      MaxUtilizableTDR_Utilised,
      AncillaryFSI_Basic_TDR_Potential,
      AncillaryFSI_Basic_TDR_Utilised,
      PremiumFSI_Potential,
      PremiumFSI_Utilised,
      AncillaryFSI_Premium_Potential,
      AncillaryFSI_Premium_Utilised,
      TotalMaxBuiltUpPotential,
      TotalMaxBuiltUpUtilised,
      permissibleCheck,
      fsiUsageNote,
      AncillaryFSI_Utilised,
      PremiumArea_Utilised,
      ExcessDevelopmentRight,
      resAncillary,
      nonResAncillary,
      totalAncillaryFactor,
      resPercent,
      nonResPercent,
    };
  }, [resArea, nonResArea, tdrArea, netPlotArea, grossPlotArea, totalReservation, basicFSIFactor, premiumFSIFactor, maxTDRFactor]);

  const handleCalculate = () => setCalculated(true);

  return (
    <div className={styles.builtUpArea_sectionContainer}>
      <h3 className={styles.builtUpArea_sectionTitle}>Ancillary Factor</h3>

      {/* Input Fields */}
      <div className={styles.builtUpArea_inputGroup}>
        <label>Proposed Residential Built-up Area (Sq.m):</label>
        <input
          type="number"
          value={resArea}
          onChange={(e) => setResArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      <div className={styles.builtUpArea_inputGroup}>
        <label>Proposed Non-Residential Built-up Area (Sq.m):</label>
        <input
          type="number"
          value={nonResArea}
          onChange={(e) => setNonResArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      {/* Readonly */}
      <div className={styles.builtUpArea_readOnlyFields}>
        <div className={styles.builtUpArea_readOnlyRow}>
          <span>Proposed Total Built-up Area (Sq.m):</span>
          <span>{totalBuiltUp}</span>
        </div>
      </div>

      {/* Ancillary Factor Table */}
      <div className={styles.builtUpArea_tableBox}>
        <table className={styles.builtUpArea_customTable}>
          <thead>
            <tr>
              <th></th>
              <th>Residential</th>
              <th>Non-Residential</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Built-up Area (P-Line Area):</td>
              <td>{Number(resArea || 0).toFixed(2)}</td>
              <td>{Number(nonResArea || 0).toFixed(2)}</td>
              <td>{totalBuiltUp.toFixed(2)}</td>
            </tr>
            <tr>
              <td>% of Total Built-up Area:</td>
              <td>{(results.resPercent * 100).toFixed(2)}</td>
              <td>{(results.nonResPercent * 100).toFixed(2)}</td>
              <td>100</td>
            </tr>
            <tr>
              <td>Ancillary Factor:</td>
              <td>{results.resAncillary.toFixed(1)}</td>
              <td>{results.nonResAncillary.toFixed(1)}</td>
              <td>{results.totalAncillaryFactor.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Purchased TDR */}
      <div className={styles.builtUpArea_inputGroup}>
        <label>Purchased TDR Area (Sq.m):</label>
        <input
          type="number"
          value={tdrArea}
          onChange={(e) => setTdrArea(e.target.value)}
          placeholder="Enter Value"
        />
      </div>

      {/* Error */}
      <div className={styles.builtUpArea_errorBox}>
        Maximum {results.MaxPermissibleTDR?.toFixed(2)} sq.m TDR can be purchased
      </div>

      {/* Button */}
      <button className={styles.builtUpArea_calculateBtn} onClick={handleCalculate}>
        Calculate
      </button>

      {calculated && (
        <>
          <div className={styles.builtUpArea_fsiSection}>

            {/* ---- Read-only Calculations ---- */}
            <div className={styles.builtUpArea_readOnlyFields}>
              
              <div className={styles.builtUpArea_readOnlyRow}>
                <span>Net Plot Area (Sqm):</span>
                <span>{netPlotArea.toFixed(2)}</span>
              </div>
            </div>


            <h4>FSI Calculation Table</h4>

            <div className={styles.builtUpArea_tableBox}>
              <table className={styles.builtUpArea_customTable}>
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Built Up Area Potential</th>
                    <th>Utilised Built Up Area</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic FSI</td>
                    <td>{results.BasicFSI_Potential.toFixed(2)}</td>
                    <td>{results.BasicFSI_Utilised.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>- TDR against Reservation</td>
                    <td>{results.ReservationTDR.toFixed(2)}</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>- Total TDR Area available</td>
                    <td>{results.TotalTDRAvailable.toFixed(2)}</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>- Max. Permissible TDR Loading</td>
                    <td>{results.MaxPermissibleTDR.toFixed(2)}</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td>Max Utilizable TDR Area</td>
                    <td>{results.MaxUtilizableTDR.toFixed(2)}</td>
                    <td>{results.MaxUtilizableTDR_Utilised.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Ancillary FSI for (Basic FSI + TDR)</td>
                    <td>{results.AncillaryFSI_Basic_TDR_Potential.toFixed(2)}</td>
                    <td>{results.AncillaryFSI_Basic_TDR_Utilised.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Premium FSI</td>
                    <td>{results.PremiumFSI_Potential.toFixed(2)}</td>
                    <td>{results.PremiumFSI_Utilised.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Ancillary FSI for Premium FSI</td>
                    <td>{results.AncillaryFSI_Premium_Potential.toFixed(2)}</td>
                    <td>{results.AncillaryFSI_Premium_Utilised.toFixed(2)}</td>
                  </tr>
                  <tr className={styles.builtUpArea_totalRow}>
                    <td><strong>Max Built Up Area Potential</strong></td>
                    <td><strong>{results.TotalMaxBuiltUpPotential.toFixed(2)}</strong></td>
                    <td><strong>{results.TotalMaxBuiltUpUtilised.toFixed(2)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Extra Calculations */}
            <div className={styles.builtUpArea_tableBox}>
              <table className={styles.builtUpArea_customTable}>
                <tbody>
                  <tr>
                    <td>Ancillary FSI to be Utilised</td>
                    <td>{results.AncillaryFSI_Utilised.toFixed(2)} Sq.m</td>
                  </tr>
                  <tr>
                    <td>Premium FSI to be Utilised</td>
                    <td>{results.PremiumArea_Utilised.toFixed(2)} Sq.m</td>
                  </tr>
                  <tr>
                    <td>Excess Development Rights eligible for DRC</td>
                    <td>{results.ExcessDevelopmentRight.toFixed(2)} Sq.m</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes Section */}

          <div
            className={
              results.permissibleCheck.includes("within permissible")
                ? `${styles.builtUpArea_noteBox} ${styles.builtUpArea_noteBox_success}`
                : styles.builtUpArea_noteBox
            }
          >
            <p>
              <strong>Note:</strong> {results.permissibleCheck}
            </p>
          </div>

          <div
            className={
              !results.fsiUsageNote.includes("not")
                ? `${styles.builtUpArea_noteBox} ${styles.builtUpArea_noteBox_success}`
                : styles.builtUpArea_noteBox
            }
          >
            <p>
              <strong>Note:</strong> {results.fsiUsageNote}
            </p>
          </div>


          {/* <div className={styles.builtUpArea_noteBoxLight}>
            <p>
              <strong>Note:</strong> {results.fsiUsageNote}
            </p>
          </div> */}


        </>
      )}

      {/* Back Button */}
      <button className={styles.builtUpArea_prevButton} onClick={onPrev}>
        ← Back
      </button>
    </div>
  );
};

export default AncillaryFactorCalculator;
